import type { ParsedSymbol } from '@/features/chart/lib/configs/options-config'
import {
  getExpiryTimestamp,
  isExpiryStillAvailable,
  resolveOptionSymbolForExchange,
} from '@/features/chart/lib/utils/option-util'
import {
  resolveBaseCurrency,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { isInverseInstrument } from '@/lib/instrument-util'
import { orderQuantitySide } from '@/lib/order-denomination'
import { getInstrumentMapForExchange } from '@/lib/utils/exchange-instrument-map'
import { type OrderFormDenomPreference, OrderFormSummaryTab } from '@/state/order'
import { AccountBinding, SelectedInstrument, SelectedOption } from '@/types/dashboard'
import { AlgoState, AlgoType } from '@/types/generated/algo'
import { Order, OrderSide, OrderType } from '@/types/generated/common/trading'
import { Exchange, Instrument } from '@/types/generated/common/types'
import { ExecutionAnalyticsTargetType, UnifiedOrderItem } from '@/types/generated/public_api'

export type OrderRowTickerType = 'spot' | 'perps' | 'options' | 'future'

export interface OrderRowTicker {
  symbol: string
  isOption: boolean
  tickerType: OrderRowTickerType
  displaySymbol: string
  baseCurrency: string
}

// Symbol/exchange-based ticker resolution, shared by resolveOrderRowTicker
// (below) and by row types that don't carry a full Order — a hedge's
// instrument, a trigger condition's referenced instrument, an RFQ leg.
export function resolveSymbolTicker(
  symbol: string,
  exchange: Exchange,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>
): OrderRowTicker {
  const upper = symbol.toUpperCase()
  const isOption = resolveOptionSymbolForExchange(exchange, symbol, optionSymbolMap) !== null
  let tickerType: OrderRowTickerType = 'spot'
  if (upper.includes('PERPETUAL') || upper.includes('-PERP')) {
    tickerType = 'perps'
  } else if (isOption) {
    tickerType = 'options'
  } else if (/\d{1,2}[A-Z]{3}\d{2}/i.test(symbol)) {
    tickerType = 'future'
  }
  const baseCurrency = resolveBaseCurrency(
    getInstrumentMapForExchange(multiExchangeInstrumentMap, exchange),
    symbol
  )
  const displaySymbol = tickerType === 'options' ? baseCurrency.toUpperCase() : symbol
  return { symbol, isOption, tickerType, displaySymbol, baseCurrency }
}

// A dated contract's expiry token as its own dash/underscore-delimited segment
// ("BTC-28JUN24", "BTC-28JUN24-70000-C-USDT").
const DATED_SYMBOL_EXPIRY = /(?:^|[-_])(\d{1,2}[A-Z]{3}\d{2})(?=$|[-_])/i

/**
 * Whether `symbol` is a dated contract (option or future) past its expiry, so a history
 * row on it must not be loaded into the order form. Prefers the exchange's own
 * expirationTsMs; an expired contract has usually dropped out of the instrument list, so
 * it falls back to the option map and then the symbol's own date token. Perps/spot (no
 * expiry) are never expired.
 */
export function isOrderRowSymbolExpired(
  symbol: string,
  exchange: Exchange,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>
): boolean {
  const instrument = getInstrumentMapForExchange(multiExchangeInstrumentMap, exchange).get(symbol)
  const instrumentTs = Number(instrument?.expirationTsMs)
  if (Number.isFinite(instrumentTs) && instrumentTs > 0) return instrumentTs <= Date.now()

  const option = resolveOptionSymbolForExchange(exchange, symbol, optionSymbolMap)
  const optionTs = Number(option?.expirationTsMs)
  if (Number.isFinite(optionTs) && optionTs > 0) return optionTs <= Date.now()

  const expiry = (option?.expiry ?? symbol.match(DATED_SYMBOL_EXPIRY)?.[1])?.toUpperCase()
  // parseExpiryDate falls back to epoch 0 for an unreadable token — not a real expiry.
  if (!expiry || getExpiryTimestamp(expiry) <= 0) return false
  return !isExpiryStillAvailable(expiry)
}

export function resolveOrderRowTicker(
  order: Order,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>
): OrderRowTicker {
  return resolveSymbolTicker(
    order.symbol,
    order.exchange,
    multiExchangeInstrumentMap,
    optionSymbolMap
  )
}

/**
 * An order's quantity in the unit the order form reads it in (the card's Base/Quote
 * setting, else the isInverseInstrument rule for 'auto'), converted through the fill or
 * limit price. Undefined when a conversion is needed but there's no price; UNSPECIFIED
 * orders pass through unchanged.
 */
export function resolveRepeatAmount(
  order: Pick<Order, 'symbol'> &
    Partial<Pick<Order, 'quantity' | 'denomination' | 'avgFillPrice' | 'price' | 'exchange'>>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>,
  denomPreference: OrderFormDenomPreference = 'auto'
): string | undefined {
  if (!order.quantity) return undefined
  const orderUnit = orderQuantitySide(order.denomination)
  if (!orderUnit) return order.quantity
  const exchange = order.exchange ?? Exchange.EXCHANGE_UNSPECIFIED
  if (resolveOptionSymbolForExchange(exchange, order.symbol, optionSymbolMap)) {
    return order.quantity
  }
  const instrument =
    getInstrumentMapForExchange(multiExchangeInstrumentMap, exchange).get(order.symbol) ?? null
  const formUnit =
    denomPreference !== 'auto'
      ? denomPreference
      : isInverseInstrument(order.symbol, instrument)
        ? 'quote'
        : 'base'
  if (orderUnit === formUnit) return order.quantity

  const quantity = Number(order.quantity)
  const price = Number(order.avgFillPrice) || Number(order.price)
  if (!Number.isFinite(quantity) || !(price > 0)) return undefined
  const converted = orderUnit === 'quote' ? quantity / price : quantity * price
  return String(Number(converted.toPrecision(8)))
}

/** Rebind existing legs while preserving edits; unchanged additions keep their array identity. */
export function upsertOrderRowAccount<
  T extends { accountId?: string; accountBinding?: AccountBinding },
>(rows: T[], incoming: T, matches: (row: T) => boolean): T[] {
  let found = false
  let changed = false
  const next = rows.map((row) => {
    if (!matches(row)) return row
    found = true
    const currentBinding = row.accountBinding
    const nextBinding = incoming.accountBinding
    const sameBinding =
      currentBinding?.kind === nextBinding?.kind &&
      (currentBinding?.kind !== 'scoped' ||
        (nextBinding?.kind === 'scoped' && currentBinding.reason === nextBinding.reason))
    if (row.accountId === incoming.accountId && sameBinding) return row
    changed = true
    return { ...row, accountId: incoming.accountId, accountBinding: nextBinding }
  })
  if (!found) return [...rows, incoming]
  return changed ? next : rows
}

export type OrderRowSelection = { option: SelectedOption } | { instrument: SelectedInstrument }

interface InstrumentSelectionInput {
  symbol: string
  exchange: Exchange
  side: OrderSide
  // SelectedOption.price is required — historical rows without a real quote
  // (a spreader leg, a trigger's dispatch order) pass '0' explicitly; the
  // order form's live ticker subscription supplies the real price afterward,
  // same as `lastPrice` being left unset on the instrument branch below.
  price: string
  amount?: string
  accountId?: string
  spreaderRole?: 'quoting' | 'taking'
}

/**
 * Shared core behind buildOrderRowSelection/buildSpreaderLegSelection/
 * buildDispatchOrderSelection — every caller ultimately resolves to one
 * symbol+exchange+side (+ optional account/spreader-role tag), so the
 * option-vs-instrument branching and field derivation lives here once.
 */
function buildInstrumentSelectionCore(
  input: InstrumentSelectionInput,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>
): OrderRowSelection {
  const { symbol, exchange, side, price, amount, accountId, spreaderRole } = input
  const { isOption } = resolveSymbolTicker(
    symbol,
    exchange,
    multiExchangeInstrumentMap,
    optionSymbolMap
  )
  const isSell = side === OrderSide.ORDER_SIDE_SELL

  // `accountId` present means the caller (a spreader-leg carryover, a trigger's dispatch
  // order, or an order row's "Add to Orderform" — see bindAccount) is duplicating an
  // already-account-bound row; absent (e.g. Repeat) means the row follows the form's own
  // resolution.
  const accountBinding: AccountBinding = accountId
    ? { kind: 'scoped', reason: 'existing-order' }
    : { kind: 'inherited' }

  if (isOption) {
    return {
      option: {
        instrumentName: symbol,
        accountBinding,
        side: isSell ? 'bid' : 'ask',
        tradeSide: isSell ? 'sell' : 'buy',
        price,
        amount: amount || undefined,
        accountId,
        spreaderRole,
        // Same reasoning as the instrument branch below: without this, the order
        // form has no way to know this leg's own exchange and falls back to the
        // active account's primaryExchange when resolving its instrument
        // metadata (base currency, tick size, etc.) — silently mixing in another
        // exchange's data for a cross-exchange leg (e.g. a Bybit option shown
        // while a Deribit account is active).
        exchange: exchange.toString(),
      },
    }
  }

  const scopedMap = getInstrumentMapForExchange(multiExchangeInstrumentMap, exchange)
  const baseCurrency = resolveBaseCurrency(scopedMap, symbol)
  const quoteCurrency = resolveQuoteCurrency(scopedMap, symbol)
  const upper = symbol.toUpperCase()
  const instrumentKind =
    upper.includes('PERPETUAL') || upper.includes('-PERP')
      ? 'perp'
      : /\d{1,2}[A-Z]{3}\d{2}/i.test(symbol)
        ? 'future'
        : 'spot'

  return {
    instrument: {
      symbol,
      accountBinding,
      baseCurrency,
      quoteCurrency,
      instrumentKind,
      side: isSell ? 'sell' : 'buy',
      // NOT a reference price — `price` (when set) is a historical fill/limit
      // price, which can be arbitrarily stale. Leave lastPrice unset so the
      // order form's live ticker subscription supplies the real price.
      lastPrice: undefined,
      amount: amount || undefined,
      exchange: exchange.toString(),
      type: scopedMap.get(symbol)?.type,
      accountId,
      spreaderRole,
    },
  }
}

/**
 * Builds the SelectedOption/SelectedInstrument the order form needs to add this
 * order's instrument. `includeAmount: true` also carries over the order's price and
 * quantity (in the form's unit, see resolveRepeatAmount) ("Repeat"); `false` leaves the
 * amount blank so the user enters their own ("Add to Orderform").
 */
export function buildOrderRowSelection(
  order: Order,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>,
  opts: {
    includeAmount: boolean
    denomPreference?: OrderFormDenomPreference
    // Bind the leg to the order's own account (scoped) instead of the form's resolution.
    bindAccount?: boolean
  }
): OrderRowSelection {
  return buildInstrumentSelectionCore(
    {
      symbol: order.symbol,
      accountId: opts.bindAccount ? order.accountId || undefined : undefined,
      exchange: order.exchange,
      side: order.side,
      price: order.avgFillPrice || order.price || '0',
      amount: opts.includeAmount
        ? resolveRepeatAmount(
            order,
            multiExchangeInstrumentMap,
            optionSymbolMap,
            opts.denomPreference
          )
        : undefined,
    },
    multiExchangeInstrumentMap,
    optionSymbolMap
  )
}

/**
 * One Spreader leg (quoting or taking) → one order-form selection, tagged
 * with the same `spreaderRole` the leg carries — presence of that tag on the
 * pushed selections is what puts the order form into spreader mode (see
 * isSpreaderMode in use-unified-order-form.ts), no separate "activate" call
 * needed. `leg.quantity` is carried over as-is; it does NOT normalize against
 * `leg.denomination`, so a quote-denominated leg's size may need a manual
 * check against the destination form's default (base-denominated) unit.
 */
export function buildSpreaderLegSelection(
  leg: {
    symbol: string
    exchange: Exchange
    side: OrderSide
    quantity: string
    accountId: string
    role: 'quoting' | 'taking' | undefined
  },
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>
): OrderRowSelection {
  return buildInstrumentSelectionCore(
    {
      symbol: leg.symbol,
      exchange: leg.exchange,
      side: leg.side,
      price: '0',
      amount: leg.quantity,
      accountId: leg.accountId,
      spreaderRole: leg.role,
    },
    multiExchangeInstrumentMap,
    optionSymbolMap
  )
}

/**
 * A trigger's dispatch action, decoded to a plain single order (see
 * OrderExecutionCommand.new in execution.proto), pushed as one order-form
 * selection. Exchange isn't on the command itself — trigger-utils.ts callers
 * pass the trigger's own TriggerState.exchange (a trigger is single-venue).
 */
export function buildDispatchOrderSelection(
  newOrder: { symbol: string; orderSide: OrderSide; quantity: string; accountId: string },
  exchange: Exchange,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>,
  optionSymbolMap: Map<Exchange, Map<string, ParsedSymbol>>
): OrderRowSelection {
  return buildInstrumentSelectionCore(
    {
      symbol: newOrder.symbol,
      exchange,
      side: newOrder.orderSide,
      price: '0',
      amount: newOrder.quantity,
      accountId: newOrder.accountId,
    },
    multiExchangeInstrumentMap,
    optionSymbolMap
  )
}

/**
 * The order-form tab matching an order row's own type — the algo's type when the row is an
 * algo (TWAP, chase…), else the venue order type. Undefined for types the form has no tab for
 * (liquidations, hedges, triggers), so the caller leaves the current tab alone.
 */
export function resolveOrderFormTab(
  order: Pick<Order, 'orderType'>,
  algoType?: AlgoType
): OrderFormSummaryTab | undefined {
  if (algoType !== undefined) {
    switch (algoType) {
      case AlgoType.ALGO_TYPE_LIMIT:
        return OrderFormSummaryTab.Limit
      case AlgoType.ALGO_TYPE_MARKET:
        return OrderFormSummaryTab.Market
      case AlgoType.ALGO_TYPE_TWAP:
        return OrderFormSummaryTab.TWAP
      case AlgoType.ALGO_TYPE_ADVANCED_TWAP:
        return OrderFormSummaryTab.AdvancedTwap
      case AlgoType.ALGO_TYPE_LIMIT_CHASE:
        return OrderFormSummaryTab.LimitChase
      case AlgoType.ALGO_TYPE_SCALE:
        return OrderFormSummaryTab.Scale
      default:
        return undefined
    }
  }
  switch (order.orderType) {
    case OrderType.ORDER_TYPE_LIMIT:
      return OrderFormSummaryTab.Limit
    case OrderType.ORDER_TYPE_MARKET:
      return OrderFormSummaryTab.Market
    default:
      return undefined
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface AnalyticsTarget {
  txId: string
  targetType: ExecutionAnalyticsTargetType
  // The transaction's OWN account — a cross-account table (Open Orders / Order
  // History / Trade History) can show rows from any active account, and the
  // analytics sheet must query the row's own account, not whichever one is
  // currently selected in the header.
  accountId: string
  // Full account set for a multi-account algo (cross-exchange arb spans two
  // accounts/venues under one algo_id) — absent or single-element for every
  // other row type, where `accountId` alone already says everything.
  accountIds?: string[]
}

/** Resolves which transaction/order/algo an order row's analytics sheet should open for. */
export function resolveAnalyticsTarget(item: UnifiedOrderItem): AnalyticsTarget | null {
  const order = item.order
  if (!order) return null
  const isAlgo = order.label === 'algo' || UUID_RE.test(order.label)
  const txId = isAlgo ? item.algoState?.id || order.label : order.orderId
  if (!txId) return null
  return {
    txId,
    targetType: isAlgo
      ? ExecutionAnalyticsTargetType.EXECUTION_ANALYTICS_TARGET_TYPE_ALGO
      : ExecutionAnalyticsTargetType.EXECUTION_ANALYTICS_TARGET_TYPE_ORDER,
    accountId: order.accountId,
  }
}

/** Resolves the analytics sheet target for a Hedge/Trigger row's own AlgoState directly —
 * these row types carry the algo already, unlike Orders which need resolveAnalyticsTarget's
 * label-based algo detection. */
export function resolveAlgoAnalyticsTarget(algo: AlgoState): AnalyticsTarget | null {
  if (!algo.id) return null
  return {
    txId: algo.id,
    targetType: ExecutionAnalyticsTargetType.EXECUTION_ANALYTICS_TARGET_TYPE_ALGO,
    accountId: algo.accountIds[0] ?? '',
    accountIds: algo.accountIds,
  }
}
